import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { getBlackbaudRedirectUri } from '@feathr/blackbox';
import { Spinner } from '@feathr/components';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import { getOAuthReturnPath } from './BlackbaudOAuthCallback.utils';

import * as styles from './BlackbaudOAuthCallback.css';

function BlackbaudOAuthCallback(): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { BlackbaudRaisersEdgeIntegrations } = useStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function handleOAuthCallback(): Promise<void> {
      const params = new URLSearchParams(globalThis.location.search);
      const code = params.get('code');
      const oauthError = params.get('error');
      const encodedState = params.get('state');

      if (oauthError) {
        const errorMessage = params.get('error_description') || oauthError;
        setError(errorMessage);
        return;
      }

      if (!code) {
        setError(t('No authorization code received from Blackbaud.'));
        return;
      }

      try {
        const listResponse = BlackbaudRaisersEdgeIntegrations.list();
        await when(() => !listResponse.isPending);

        const integration = listResponse.models.length > 0 ? listResponse.models[0] : undefined;

        if (!integration) {
          setError(t('No Blackbaud integration found.'));
          return;
        }

        const redirectUri = getBlackbaudRedirectUri(globalThis.location.origin);
        await integration.authenticate(code, redirectUri);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err);
        setError(errorMessage);
        return;
      }

      history.push(
        getOAuthReturnPath(encodedState, localUrl('/settings/integrations/raisers-edge')),
      );
    }

    void handleOAuthCallback();
  }, [BlackbaudRaisersEdgeIntegrations, history, localUrl, t]);

  if (error) {
    return (
      <div className={styles.container}>
        <h2 className={styles.title}>{t('Authorization Failed')}</h2>
        <p>{error}</p>
        <a className={styles.link} href={localUrl('/settings/integrations/raisers-edge')}>
          {t('Return to Integrations')}
        </a>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Spinner />
      <p>{t('Connecting to Blackbaud...')}</p>
    </div>
  );
}

export default observer(BlackbaudOAuthCallback);
